/**
 * Auth feature-local constants (ADR-0009): error codes the UI branches on and the
 * client-facing policy numbers. Cross-cutting values (route paths) live in
 * `@/config/routes`; these are auth-specific and exported via the feature barrel.
 */

/** Stable error codes returned by the admin auth realm (ADR-0008). Branch on these, never raw strings. */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_TEMPORARILY_LOCKED: 'ACCOUNT_TEMPORARILY_LOCKED',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  OTP_INVALID: 'OTP_INVALID',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  RESEND_COOLDOWN_ACTIVE: 'RESEND_COOLDOWN_ACTIVE',
  INVALID_PHONE_NUMBER: 'INVALID_PHONE_NUMBER',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
  RESET_TOKEN_EXPIRED: 'RESET_TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  PASSWORD_MUST_BE_DIFFERENT: 'PASSWORD_MUST_BE_DIFFERENT',
  /** A DTO rejection (ADR-0008); the per-field reason is in `details[]`, not `message` (ADO 121341). */
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  /** A mid-session refresh 401 whose account was deactivated (ADO 118305) — carried on the
   *  original request's own body, since `ActiveUserGuard` re-validates account status on every
   *  request, not just refresh. */
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/** Client-facing auth policy. Server is the source of truth; these drive UI defaults/timers. */
export const AUTH_POLICY = {
  /** Idle inactivity timeout for non-remember-me sessions (story 101634). */
  idleMs: 20 * 60 * 1000,
  /**
   * How often a live session renews its refresh cookie.
   *
   * The cookie is only 600s (measured against QA 2026-09-15: `Max-Age=600`, rotated and slid
   * forward on every use) and the access token only 300s, so a session that makes no request
   * for ten minutes is unrecoverable — the client cannot refresh, and the 20-minute
   * `idleMs` timer above can never fire first. Refreshing reactively on a 401 is therefore not
   * enough on its own: an admin reading one screen, or one whose current module is mocked and
   * issues no real request, is thrown out while apparently active. Four minutes sits inside
   * both windows with margin.
   */
  keepAliveMs: 4 * 60 * 1000,
  /** Default OTP resend cooldown when the server doesn't supply one. */
  resendCooldownSeconds: 60,
  /** Max OTP resends per challenge. */
  maxResends: 5,
} as const;
